import React, { useContext, useEffect, useRef, useState } from 'react';
import { ActionIcon, Menu } from '@mantine/core';
import { Dots, Download, Edit, Eye, Photo, Trash } from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import TableView from '../../common/TableView';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import { triggerNotification } from '../../../helpers/notification';
import HeaderView from '../../common/HeaderView';
import AppStack from '../../common/AppStack';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import AppMenu from '../../common/AppMenu';
import { formatUtcDate } from '../../../helpers/format';
import { downloadUrlFile } from '../../../helpers/awsHelper';
import DeleteMediaModal from '../media/DeleteMediaModal';
import EditMediaModal from '../media/EditMediaModal';

const TABLE_COLUMNS = [
  {
    label: 'File name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Date created',
    value: 'created',
    sortable: true
  }
];

const VIEW_ACTIONS = [
  {
    label: 'View',
    value: 'view',
    icon: <Eye size={18} />,
    section: 1
  },
  {
    label: 'Download',
    value: 'download',
    icon: <Download size={18} />,
    section: 1
  },
  {
    label: 'Edit',
    value: 'edit',
    icon: <Edit size={18} />,
    section: 1
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.DELETE,
    icon: <Trash color="red" size={18} />,
    section: 2,
    color: 'red'
  }
];

const StoreMediaTableView = () => {
  const hasFetched = useRef(false);
  const { state: authState } = useContext(AuthContext);
  const { state, fetchPaginatedStoreMedia } = useContext(StoreContext);
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    { search: '' },
    (newFilter) => {
      fetchPaginatedStoreMedia(
        authState.pkEcomStore,
        newFilter,
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedEcomStoreMedia
  );
  const loading = !hasFetched.current || dataLoading;
  const noMediaCreated = !loading && totalNoFilterCount === 0;
  const selectedData = data.filter((f) =>
    selectedRows.includes(f.pkEcomStoreMedia)
  );

  const mediaFilter = filter ? { search: filter.search } : null;

  useEffect(() => {
    if (authState.pkEcomStore) {
      fetchPaginatedStoreMedia(authState.pkEcomStore, {}, null, (error) => {
        triggerNotification(error);
      });
      hasFetched.current = true;
    }
  }, [authState.pkEcomStore]);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'Media',
          to: '/merchant/content/media'
        }
      ]}
    >
      <TableView
        columns={TABLE_COLUMNS}
        disabled={noMediaCreated}
        disabledContent={{
          title: 'No media added yet',
          description: 'Media will appear here once uploaded.',
          icon: <Photo color="#000" size={125} />
        }}
        emptyMessage="No media found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by name',
            value: filter.search,
            onChange: (value) => onFilterChange({ ...filter, search: value })
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={!hasFetched.current || loading}
        noSort
        onAction={(action, item) => {
          const currentData = data.find((s) => s.pkEcomStoreMedia === item.key);
          if (currentData) {
            switch (action) {
              case 'view':
                window.open(currentData.src, '_blank');
                break;
              case 'download':
                downloadUrlFile(
                  currentData.src,
                  currentData.alt,
                  currentData.contentType
                );
                break;
              case VIEW_ACTIONS_ENUM.DELETE:
              default:
                onOpenModal(action, [currentData]);
                break;
            }
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={data.map((a) => ({
          key: a.pkEcomStoreMedia,
          uuid: a.uuid,
          actions: VIEW_ACTIONS,
          columns: [
            {
              key: 1,
              label: a.alt,
              weight: 500,
              subLabel: a.contentType,
              src: a.src ?? null,
              showImage: true,
              component: 'a',
              href: a.src,
              target: '_blank',
              onClick: (e) => {
                e.stopPropagation();
              }
            },
            {
              key: 2,
              label: dayjs(formatUtcDate(a.createdAt)).format('MMM D, YYYY')
            }
          ]
        }))}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal('bulk_delete', [...selectedData]);
                }}
                style={{ color: 'red' }}
              >
                Delete media
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedData.map((p) => p.pkEcomStoreMedia)}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Media"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <DeleteMediaModal
        filter={mediaFilter}
        fkEcomStore={authState.pkEcomStore}
        isOpen={
          modalState.isOpen &&
          (modalState.action === 'bulk_delete' ||
            modalState.action === VIEW_ACTIONS_ENUM.DELETE)
        }
        media={
          modalState.action === VIEW_ACTIONS_ENUM.DELETE
            ? modalState.item
            : selectedData
        }
        onClose={onCloseModal}
        onSuccess={() => {
          onRefresh();
          setSelectedRows([]);
        }}
        totalItemCount={
          modalState.action === VIEW_ACTIONS_ENUM.DELETE ? 1 : totalCount
        }
      />

      <EditMediaModal
        fkEcomStore={authState.pkEcomStore}
        isOpen={modalState.isOpen && modalState.action === 'edit'}
        media={modalState.action === 'edit' ? modalState.item[0] : null}
        onClose={onCloseModal}
        onSuccess={() => {
          onCloseModal();
          onRefresh();
        }}
      />
    </HeaderView>
  );
};

export default StoreMediaTableView;
